import React from 'react'
import styled from '@emotion/styled'
import { Button, Progress, Spin, Tooltip, Upload } from 'antd'
import {
  LinkOutlined,
  LoadingOutlined,
  PaperClipOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { RcFile } from 'antd/lib/upload/interface'
import { UploadRequestOption } from 'rc-upload/lib/interface'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useMeContext } from '@organice/contexts'
import { useServiceModulePermissions } from '@organice/contexts/serviceDisplay'
import { Person } from '../person'
import { formatFileName } from './file-name'
import { UploadFile, UploadFileVersion } from './types'
import { VersionHistoryPopover } from './version-history-popover'

export type FileItemActions = {
  download: () => void
  preview: () => void
  remove: () => void
}

interface RegularFileEntryProps {
  file: UploadFile
  actions: FileItemActions
  isConfiguration?: boolean
  beforeUpload?: (file: RcFile) => boolean | typeof Upload.LIST_IGNORE
  onUploadNewVersion?: (
    options: UploadRequestOption,
    uploadId: string | number
  ) => void
  onDeleteVersion?: (
    uploadId: string | number,
    versionUid: string | number
  ) => void
}

export const RegularFileEntry: React.FC<RegularFileEntryProps> = ({
  file,
  actions,
  isConfiguration = false,
  beforeUpload,
  onUploadNewVersion,
  onDeleteVersion,
}) => {
  const serviceModulePermissions = useServiceModulePermissions()
  const {
    state: { me },
  } = useMeContext()
  const { t } = useTranslation()

  const isNewFile = file['percent'] !== undefined
  const uploadedBy = isNewFile ? me : file.uploadedBy

  const fileName = formatFileName(file.name, file.extension)

  const allVersions: UploadFileVersion[] = [
    {
      uid: file.uid,
      name: file.name,
      url: file.url,
      size: file.size,
      extension: file.extension,
      uploadedBy: file.uploadedBy,
      createdAt: file.createdAt,
      isNewVersion: file.isNewVersion,
    },
    ...(file.versions || []),
  ]
  const hasVersions = allVersions.length > 0
  const canUploadVersion =
    file.uploadId !== undefined &&
    (isConfiguration || serviceModulePermissions.writable)
  const uploadVersionDisabled = !canUploadVersion
  const canDeleteAll = isConfiguration || serviceModulePermissions.writable

  return (
    <FileEntryWrapper>
      <AttrValueFileListItem>
        <VersionBtnSlot>
          {hasVersions && (
            <VersionHistoryPopover
              versions={allVersions}
              uploadId={file.uploadId}
              onDeleteVersion={onDeleteVersion}
              onDeleteAll={() => actions.remove()}
              deleteAllDisabled={!canDeleteAll}
            />
          )}
        </VersionBtnSlot>
        <ColFilename>
          {file.status === 'uploading' ? (
            <Spin indicator={<LoadingOutlined />} />
          ) : (
            <PaperClipOutlined />
          )}
          <a
            target="_blank"
            rel="noopener noreferrer"
            className="ant-upload-list-item-name"
            title={fileName}
            href={file.url}
            style={{ display: 'block' }}
          >
            {fileName}
          </a>
        </ColFilename>

        <ColUploadedAt>
          {isNewFile
            ? t('service.attribute.upload.justNow')
            : file.createdAt
              ? dayjs(file.createdAt).format('DD.MM.YY HH:mm')
              : null}
        </ColUploadedAt>
        <ColUploadedBy>
          {uploadedBy ? <Person user={uploadedBy} /> : null}
        </ColUploadedBy>
        <ColFileActions>
          {onUploadNewVersion && (
            <Tooltip
              title={t('service.attribute.upload.uploadNewVersion', {
                defaultValue: 'Upload new version',
              })}
              mouseLeaveDelay={0}
              destroyTooltipOnHide
              overlayStyle={{ pointerEvents: 'none' }}
            >
              <Upload
                className="version-upload-control"
                disabled={uploadVersionDisabled}
                showUploadList={false}
                beforeUpload={beforeUpload}
                customRequest={opts => {
                  if (file.uploadId === undefined) return
                  onUploadNewVersion(opts, file.uploadId)
                }}
              >
                <Button
                  className="file-action-button"
                  disabled={uploadVersionDisabled}
                  icon={<UploadOutlined />}
                  size="small"
                />
              </Upload>
            </Tooltip>
          )}
        </ColFileActions>
        {file.status === 'uploading' && (
          <UploadProgressBar>
            <Progress strokeWidth={2} percent={file.percent} showInfo={false} />
          </UploadProgressBar>
        )}
      </AttrValueFileListItem>
    </FileEntryWrapper>
  )
}

export const renderRegularFileEntry = (
  file: UploadFile,
  actions: FileItemActions,
  isConfiguration = false,
  onUploadNewVersion?: (
    options: UploadRequestOption,
    uploadId: string | number
  ) => void
): React.ReactNode => {
  return (
    <RegularFileEntry
      file={file}
      actions={actions}
      isConfiguration={isConfiguration}
      onUploadNewVersion={onUploadNewVersion}
    />
  )
}

export const renderAttributeFileEntry = (file: UploadFile): React.ReactNode => {
  const fileName = formatFileName(file.name, file.extension)

  return (
    <AttrFileListItem>
      <ColFilename>
        <LinkOutlined />
        <a
          target="_blank"
          rel="noopener noreferrer"
          className="ant-upload-list-item-name"
          title={fileName}
          href={file.url}
        >
          {fileName}
        </a>
      </ColFilename>
    </AttrFileListItem>
  )
}

const FileEntryWrapper = styled.div({
  position: 'relative',
  width: '100%',
})

const VersionBtnSlot = styled.div(({ theme }) => ({
  gridColumn: '1',
  gridRow: '1',
  width: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#fff',
  '.ant-btn': {
    width: 24,
    minWidth: 24,
    height: 24,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  [theme.breakpoints[3].mq]: {
    position: 'static',
    gridColumn: '1',
    gridRow: '1',
    width: 28,
  },
}))

const AttrValueFileListItem = styled.div(({ theme }) => ({
  width: '100%',
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: '28px 92px minmax(0, 1fr) auto',
  gap: 2,
  padding: '5px 0',
  '> div': {
    backgroundColor: '#fff',
  },
  ':hover': {
    '> div': {
      backgroundColor: '#f9f9f9',
    },
  },
  [theme.breakpoints[3].mq]: {
    gridTemplateColumns: '28px minmax(0, 1fr) 120px 145px auto',
    padding: '2px 0',
  },
}))

const AttrFileListItem = styled.div({
  width: '100%',
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: 10,
  padding: '2px 0',
  ':hover': {
    '> div': {
      backgroundColor: '#f9f9f9',
    },
  },
  a: {
    padding: '0 8px',
  },
})

const ColFilename = styled.div(({ theme }) => ({
  display: 'flex',
  gap: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  padding: '2px 5px',
  gridColumn: '2 / -1',
  [theme.breakpoints[3].mq]: {
    gridColumn: 'span 1',
  },
}))

const ColUploadedBy = styled.div(({ theme }) => ({
  padding: '2px 5px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  gridColumn: '3 / 4',
  fontSize: theme.fontSizeSm,
  [theme.breakpoints[3].mq]: {
    gridColumn: 'span 1',
    fontSize: theme.fontSizeRegular,
  },
}))

const ColUploadedAt = styled.div(({ theme }) => ({
  padding: '2px 5px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  gridColumn: '1 / span 2',
  fontSize: theme.fontSizeSm,
  [theme.breakpoints[3].mq]: {
    gridColumn: 'auto',
    fontSize: theme.fontSizeRegular,
  },
}))

const ColFileActions = styled.div({
  justifySelf: 'end',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  backgroundColor: 'transparent!important',
  '.ant-upload-wrapper, .ant-upload': {
    display: 'inline-flex',
    alignItems: 'center',
    width: 'auto',
  },
  '.version-upload-control, .version-upload-control .ant-upload, .version-upload-control .ant-upload-select':
    {
      flex: '0 0 auto',
      width: 'auto!important',
    },
  '.version-upload-control.ant-upload-disabled, .version-upload-control .ant-upload-disabled':
    {
      display: 'inline-flex!important',
    },
  '.ant-upload .file-action-button, .file-action-button': {
    flex: '0 0 24px',
    width: '24px!important',
    minWidth: 24,
    height: 24,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
})

const UploadProgressBar = styled.div({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  height: 2,
  overflow: 'hidden',
  lineHeight: 0,
  zIndex: 1,
  '.ant-progress': {
    display: 'block',
    fontSize: 0,
    lineHeight: 0,
  },
  '.ant-progress-outer, .ant-progress-inner': {
    display: 'block',
    lineHeight: 0,
  },
})
